import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  LoginOutlined as LoginIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { message } from 'antd';

// LINE Login Channel 設定
const LINE_LOGIN_CHANNEL_ID = '2000288067'; // 與後端 .env 一致
const LINE_LOGIN_REDIRECT_URI = 'https://app.li.com.tw/line-callback';

export default function LoginPage() {
  const [status, setStatus] = useState('LINE 登入中...');
  const [isRetrying, setIsRetrying] = useState(false);

  // 添加 timeout 機制
  const withTimeout = (promise, timeoutMs = 10000) => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`操作超時 (${timeoutMs}ms)`));
      }, timeoutMs);
      
      promise
        .then(resolve)
        .catch(reject)
        .finally(() => clearTimeout(timeoutId));
    });
  };

  // 重試機制
  const handleRetry = () => {
    setIsRetrying(true);
    setStatus('重新初始化中...');
    // 清除可能的殘留狀態
    localStorage.removeItem('user');
    // 重新載入頁面
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  useEffect(() => {
    // 檢查 URL 參數中的 code（LINE Login 回調）和 productId
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const productId = urlParams.get('productId');
    const error = urlParams.get('error');

    // 如果有錯誤參數，顯示錯誤訊息
    if (error) {
      setStatus('登入失敗');
      message.error('LINE 登入失敗: ' + (urlParams.get('error_description') || error));
      return;
    }

    // 如果有授權碼，進行後端驗證
    if (code) {
      setStatus('驗證授權碼中...');
      
      withTimeout(
        fetch('/api/auth/line-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, state }),
        }),
        10000
      )
      .then(async (res) => {
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.error('JSON 解析失敗:', e);
          message.error('伺服器回應格式錯誤: ' + text);
          setStatus('登入失敗，請重試');
          return;
        }
        
        if (res.ok && data.user) {
          console.log('登入成功:', data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
          
          // 如果有 productId，檢查報名狀態
          if (productId) {
            setStatus('檢查報名狀態中...');
            try {
              const ordersRes = await withTimeout(
                fetch('/api/orders', { credentials: 'include' }),
                8000
              );
              const orders = await ordersRes.json();
              const ordersArray = Array.isArray(orders) ? orders : [];
              
              // 檢查是否已報名此活動
              const hasRegistered = ordersArray.some(order => 
                order.orderItems.some(item => item.productId === parseInt(productId))
              );
              
              if (hasRegistered) {
                // 已報名，顯示取票訊息並導向首頁
                message.success('你已報名此活動！', 2);
                setTimeout(() => {
                  window.location.href = '/';
                }, 2000);
              } else {
                // 未報名，導向首頁並自動開啟報名表單
                setStatus('開啟報名表單...');
                window.location.href = `/?openForm=${productId}`;
              }
            } catch (e) {
              console.error('檢查報名狀態失敗:', e);
              message.warning('檢查報名狀態失敗，將導向首頁');
              // 發生錯誤時仍導向首頁
              setTimeout(() => {
                window.location.href = '/';
              }, 1500);
            }
          } else {
            // 沒有 productId，直接導向首頁
            setStatus('登入成功，導向首頁...');
            setTimeout(() => {
              window.location.href = '/';
            }, 1000);
          }
        } else {
          const errMsg = data.error || data.detail || text || '登入失敗';
          console.error('登入失敗:', errMsg);
          message.error('登入失敗: ' + errMsg);
          setStatus('登入失敗，請重試');
        }
      })
      .catch((err) => {
        console.error('LINE Login 驗證失敗:', err);
        setStatus('登入失敗，請重試');
        
        if (err.message.includes('超時')) {
          message.error('登入超時，請檢查網路連線後重試');
        } else {
          message.error('登入過程發生錯誤: ' + err.message);
        }
      });
    } else {
      // 沒有授權碼，導向 LINE Login OAuth
      setStatus('重導向到 LINE 登入...');
      
      const state = Date.now().toString() + (productId ? `-${productId}` : '');
      const scope = 'profile openid email';
      const redirectUri = encodeURIComponent(LINE_LOGIN_REDIRECT_URI + (productId ? `?productId=${productId}` : ''));
      
      const oauthUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${LINE_LOGIN_CHANNEL_ID}&redirect_uri=${redirectUri}&state=${state}&scope=${encodeURIComponent(scope)}`;
      
      setTimeout(() => {
        window.location.href = oauthUrl;
      }, 1000);
    }
  }, []);

  return (
    <div style={{ textAlign: 'center', marginTop: 100 }}>
      <h2>{status}</h2>
      
      {/* 除錯資訊 */}
      <div style={{ marginTop: 20, fontSize: '12px', color: '#666' }}>
        LINE Login API
      </div>
      
      {/* 重試按鈕 */}
      {(status.includes('失敗') || status.includes('錯誤')) && !isRetrying && (
        <div style={{ marginTop: 30 }}>
          <button 
            onClick={handleRetry}
            style={{
              padding: '10px 20px',
              backgroundColor: '#00C300',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            重新登入
          </button>
          
          <div style={{ marginTop: 15, fontSize: '12px', color: '#999' }}>
            如果持續失敗，請嘗試：<br/>
            1. 檢查網路連線<br/>
            2. 清除瀏覽器快取<br/>
            3. 重新開啟瀏覽器
          </div>
        </div>
      )}
      
      {isRetrying && (
        <div style={{ marginTop: 20, color: '#666' }}>
          正在重新初始化...
        </div>
      )}
    </div>
  );
}
